import { ReviewRoomGroup } from '@/src/types/hotels/details'
import BedOutlinedIcon from '@mui/icons-material/BedOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import SquareFootOutlinedIcon from '@mui/icons-material/SquareFootOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { Box, Typography } from '@mui/material'
import RoomAmenities from '../roomAmenities'
import RoomImageCarousel from '../roomImageCarousel'
import RoomVariantRowContainer, { ReviewContext } from '../roomVariantRow'

export interface RoomGroupCardProps {
    group: ReviewRoomGroup
    variant: 'mobile' | 'desktop'
    visibleOptions: ReviewRoomGroup['options']
    hiddenCount: number
    moreLabel: string
    /** Pre-formatted "{n} m²" room size, or empty. */
    roomSizeLabel: string
    /** e.g. "Up to 2 guests" — the room's capacity, not the booking's guest count. */
    occupancyLabel: string
    amenitiesPreviewCount: number
    viewAllAmenitiesLabel: string
    roomAmenitiesTitle: string
    photosCountLabel: (count: number) => string
    reviewContext: ReviewContext
    onSelect: (rId: string, arNo: string) => void
    onExpand: () => void
}

const metaText = { fontSize: 12, color: 'textColors.secondary' } as const

// Pure presentation: one room type — media + meta, amenities, its (pre-sliced) rate options
// and a show-more row. Mobile and desktop diverge structurally, so each is its own tree (same
// convention as ImageGallery / the shimmers); the room header is shared between them.
const RoomGroupCard = ({
    group,
    variant,
    visibleOptions,
    hiddenCount,
    moreLabel,
    roomSizeLabel,
    occupancyLabel,
    amenitiesPreviewCount,
    viewAllAmenitiesLabel,
    roomAmenitiesTitle,
    photosCountLabel,
    reviewContext,
    onSelect,
    onExpand
}: RoomGroupCardProps) => {
    const header = (
        <Box sx={{ p: 1.75 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'textColors.primary', textTransform: 'capitalize' }}>{group.roomName}</Typography>
            <Box sx={{ display: 'flex', columnGap: 2, rowGap: 0.5, mt: 0.75, flexWrap: 'wrap' }}>
                {group.bedType && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <BedOutlinedIcon sx={{ fontSize: 14, color: 'textColors.secondary' }} />
                        <Typography sx={metaText}>{group.bedType}</Typography>
                    </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PersonOutlineOutlinedIcon sx={{ fontSize: 14, color: 'textColors.secondary' }} />
                    <Typography sx={metaText}>{occupancyLabel}</Typography>
                </Box>
                {roomSizeLabel && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <SquareFootOutlinedIcon sx={{ fontSize: 14, color: 'textColors.secondary' }} />
                        <Typography sx={metaText}>{roomSizeLabel}</Typography>
                    </Box>
                )}
                {group.viewType && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <VisibilityOutlinedIcon sx={{ fontSize: 14, color: 'textColors.secondary' }} />
                        <Typography sx={metaText}>{group.viewType}</Typography>
                    </Box>
                )}
            </Box>

            {group.roomImages.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                    <RoomImageCarousel images={group.roomImages} alt={group.roomName} photosCountLabel={photosCountLabel} />
                </Box>
            )}

            {group.amenityGroups.length > 0 && (
                <RoomAmenities
                    variant={variant}
                    groups={group.amenityGroups}
                    previewCount={amenitiesPreviewCount}
                    viewAllLabel={viewAllAmenitiesLabel}
                    title={roomAmenitiesTitle}
                />
            )}
        </Box>
    )

    // Mobile: each rate is a white card stacked on the grey group background (RN redesign).
    if (variant === 'mobile') {
        return (
            <Box sx={{ bgcolor: 'neutral.100', border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
                {header}
                <Box sx={{ px: 1.75, pb: 1.75, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {visibleOptions.map((option, index) => (
                        <Box
                            key={`${option.rId}-${option.arNo}-${index}`}
                            sx={{ bgcolor: 'neutral.white', border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}
                        >
                            <RoomVariantRowContainer
                                option={option}
                                variant="mobile"
                                reviewContext={reviewContext}
                                onSelect={onSelect}
                                isLast
                            />
                        </Box>
                    ))}
                    {hiddenCount > 0 && (
                        <Box
                            component="button"
                            type="button"
                            onClick={onExpand}
                            sx={{ width: '100%', border: 0, bgcolor: 'transparent', py: 0.5, cursor: 'pointer' }}
                        >
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'link.main' }}>{moreLabel}</Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        )
    }

    // Desktop: rate options as divided rows inside a single card.
    return (
        <Box sx={{ bgcolor: 'neutral.white', border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
            {header}
            <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
                {visibleOptions.map((option, index) => (
                    <RoomVariantRowContainer
                        key={`${option.rId}-${option.arNo}-${index}`}
                        option={option}
                        variant="desktop"
                        reviewContext={reviewContext}
                        onSelect={onSelect}
                        isLast={index === visibleOptions.length - 1 && hiddenCount === 0}
                    />
                ))}
            </Box>
            {hiddenCount > 0 && (
                <Box
                    component="button"
                    type="button"
                    onClick={onExpand}
                    sx={{ width: '100%', border: 0, borderTop: 1, borderColor: 'divider', bgcolor: 'transparent', py: 1.25, cursor: 'pointer' }}
                >
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'link.main' }}>{moreLabel}</Typography>
                </Box>
            )}
        </Box>
    )
}

export { RoomGroupCard }
export default RoomGroupCard
